import React from 'react';
import { Icons } from '@shared/constants';

interface PushNotificationDisclosureModalProps {
  /** User previously denied — show "open settings" instead of "enable" */
  previouslyDenied: boolean;
  onEnable: () => void;
  onClose: () => void;
}

/**
 * Brief disclosure modal shown before requesting push notification permission.
 * We only get one shot at the native prompt, so this sets expectations first.
 * If the user previously denied, we direct them to Settings instead.
 */
export const PushNotificationDisclosureModal: React.FC<PushNotificationDisclosureModalProps> = ({
  previouslyDenied,
  onEnable,
  onClose,
}) => (
  <div className="fixed inset-0 z-80 flex items-end sm:items-center justify-center">
    {/* backdrop */}
    <div className="absolute inset-0 bg-growth/40" onClick={onClose} />

    {/* modal */}
    <div
      className="relative bg-cloud rounded-t-[2rem] sm:rounded-[2rem] w-full max-w-md mx-auto p-6 pb-8 shadow-xl"
      onClick={e => e.stopPropagation()}
    >
      {/* icon */}
      <div className="w-14 h-14 rounded-2xl bg-foliage/10 flex items-center justify-center mx-auto mb-4">
        <Icons.Bell className="w-7 h-7 text-foliage" />
      </div>

      <h2 className="text-lg font-serif font-semibold text-growth text-center mb-2">
        Stay connected
      </h2>

      <p className="text-sm text-growth/80 text-center mb-6 leading-relaxed">
        {previouslyDenied
          ? 'Notifications are currently turned off. You can re-enable them in your device settings.'
          : 'Push notifications bring you the updates that matter. No spam. No noise.'}
      </p>

      <button
        type="button"
        onClick={onEnable}
        className="w-full py-3.5 rounded-2xl bg-growth text-cloud font-semibold text-base mb-3"
      >
        {previouslyDenied ? 'Open settings' : 'Enable notifications'}
      </button>
      <button
        type="button"
        onClick={onClose}
        className="w-full py-2.5 text-sm text-growth/70 font-medium"
      >
        {previouslyDenied ? 'Not now' : 'No thanks'}
      </button>
    </div>
  </div>
);
